import React, { useState, useEffect } from 'react';
import {
    Layout,
    Card,
    Button,
    Modal,
    Form,
    DatePicker,
    Select,
    Input,
    Tag,
    Row,
    Col,
    Statistic,
    Upload,
    message,
    Space,
    Tooltip,
    Progress,
    Alert,
    Menu,
    Typography,
    Popconfirm
} from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
    PlusOutlined,
    CalendarOutlined,
    UploadOutlined,
    EyeOutlined,
    EditOutlined,
    DeleteOutlined,
    InfoCircleOutlined,
    DashboardOutlined,
    FileTextOutlined,
    HomeOutlined,
    ClockCircleOutlined,
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import {
    loadMyLeaveBalances,
    loadLeaveApplications,
    createLeaveApplication,
    updateLeaveApplication,
    loadWFHRequests,
    createWFHRequest,
    updateWFHRequest,
    deleteWFHRequest,
    loadMyWFHBalance,
    loadMyCompOffBalance,
    loadAttendanceRecords,
} from 'store/actions/AuthActions';
import { loadLeaveTypes } from 'store/slices/MasterSlice';
import VerifyIdentityCard from 'components/Auth/VerifyIdentityCard';
import useUrlFilters from 'hooks/useUrlFilters';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;
const { Sider, Content } = Layout;
const { Title, Text } = Typography;

// Section, balance year and pagination live in the URL (lv_ prefix: this is a tab of My Profile).
// Year has no schema default: the fallback is "now", and a link must keep its year.
const URL_FILTERS = {
    section: { type: 'string', default: 'overview' },
    year: { type: 'number' },
    page: { type: 'number', default: 1 },
    pageSize: { type: 'number', key: 'page_size', default: 10 },
    wfhPage: { type: 'number', key: 'wfh_page', default: 1 },
    wfhPageSize: { type: 'number', key: 'wfh_page_size', default: 10 },
    attPage: { type: 'number', key: 'att_page', default: 1 },
    attPageSize: { type: 'number', key: 'att_page_size', default: 10 },
};

const Leaves = ({ isProfileLocked = false, employee = null }) => {
    const dispatch = useDispatch();
    const [revealed] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalType, setModalType] = useState('apply'); // 'apply', 'view', 'edit'
    const [selectedLeave, setSelectedLeave] = useState(null);
    const [form] = Form.useForm();
    const [leaves, setLeaves] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [urlFilters, setUrlFilters] = useUrlFilters(URL_FILTERS, { prefix: 'lv_' });
    const activeSection = urlFilters.section;
    // Page sizes from the URL are clamped to what the API will return in one page
    const appPageSize = Math.min(urlFilters.pageSize, MAX_PAGE_SIZE);
    const wfhPageSize = Math.min(urlFilters.wfhPageSize, MAX_PAGE_SIZE);
    const attPageSize = Math.min(urlFilters.attPageSize, MAX_PAGE_SIZE);
    // Back to page 1 when the page size changes
    const pageSetter = (pageKey, sizeKey, size) => (p, ps) =>
        setUrlFilters({ [pageKey]: ps !== size ? 1 : p, [sizeKey]: ps });
    const setActiveSection = (value) => setUrlFilters({ section: value });
    const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState(null);
    const user = useSelector(state => state.auth.user);
    const leaveTypes = useSelector(state => state.master?.leave_types) || [];

    // Password verification for apply/edit
    const [passwordModalVisible, setPasswordModalVisible] = useState(false);
    const [passwordVerified, setPasswordVerified] = useState(false);
    const [pendingModalAction, setPendingModalAction] = useState(null); // { type, leave }

    const [appPagination, setAppPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    // Fetch leave applications for current employee
    const fetchApplications = async (page = 1, pageSize = 10) => {
        if (!employee?.id) return;
        setLoading(true);
        try {
            const res = await loadLeaveApplications(page, null, pageSize, employee.id);
            if (res?.data) {
                const results = res.data.results || res.data;
                setLeaves(Array.isArray(results) ? results : []);
                setAppPagination({ current: page, pageSize, total: res.data.count || 0 });
            }
        } catch { /* handled */ }
        setLoading(false);
    };

    // Load leave types + applications only after revealed
    useEffect(() => {
        if (revealed) dispatch(loadLeaveTypes());
    }, [dispatch, revealed]);

    useEffect(() => {
        if (revealed && employee?.id) fetchApplications(urlFilters.page, appPageSize);
    }, [employee?.id, revealed, urlFilters.page, appPageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    const showModal = (type = 'apply', leave = null) => {
        if (isProfileLocked && (type === 'apply' || type === 'edit')) {
            message.warning('Cannot modify leave applications - Profile is locked');
            return;
        }

        // Require password for apply/edit (not view)
        if ((type === 'apply' || type === 'edit') && !passwordVerified) {
            setPendingModalAction({ type, leave });
            setPasswordModalVisible(true);
            return;
        }

        openLeaveModal(type, leave);
    };

    const openLeaveModal = (type, leave) => {
        setModalType(type);
        setSelectedLeave(leave);
        setIsModalVisible(true);

        if (leave && type === 'edit') {
            form.setFieldsValue({
                type: leave.leave_type,
                dateRange: [dayjs(leave.start_date), dayjs(leave.end_date)],
                reason: leave.reason
            });
            setSelectedLeaveTypeId(leave.leave_type);
        } else if (leave && type === 'view') {
            // View modal doesn't need form values
        } else {
            form.resetFields();
            setFileList([]);
        }
    };

    const handleIdentityVerified = () => {
        setPasswordVerified(true);
        setPasswordModalVisible(false);
        if (pendingModalAction) {
            openLeaveModal(pendingModalAction.type, pendingModalAction.leave);
            setPendingModalAction(null);
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setModalType('apply');
        setSelectedLeave(null);
        setSelectedLeaveTypeId(null);
        form.resetFields();
        setFileList([]);
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const data = {
                employee: employee?.id,
                leave_type: values.type,
                start_date: values.dateRange[0].format('YYYY-MM-DD'),
                end_date: values.dateRange[1].format('YYYY-MM-DD'),
                reason: values.reason,
                leave_duration: 'full_day',
                priority: 'normal',
            };

            if (modalType === 'edit' && selectedLeave) {
                await updateLeaveApplication(selectedLeave.id, data);
                message.success('Leave application updated successfully!');
            } else {
                await createLeaveApplication(data);
                message.success('Leave application submitted successfully!');
            }

            handleCancel();
            fetchApplications(appPagination.current, appPagination.pageSize);
        } catch (err) {
            const detail = err?.response?.data;
            if (detail && typeof detail === 'object') {
                const msgs = Object.values(detail).flat().join('; ');
                message.error(msgs || 'Failed to submit leave application');
            } else {
                message.error('Failed to submit leave application');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (leaveId) => {
        if (isProfileLocked) {
            message.warning('Cannot cancel leave applications - Profile is locked');
            return;
        }
        try {
            await updateLeaveApplication(leaveId, { status: 'withdrawn' });
            message.success('Leave application withdrawn successfully!');
            fetchApplications(appPagination.current, appPagination.pageSize);
        } catch {
            message.error('Failed to withdraw leave application');
        }
    };

    const handleFileChange = ({ fileList: newFileList }) => {
        setFileList(newFileList);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'green';
            case 'pending': return 'orange';
            case 'rejected': return 'red';
            default: return 'default';
        }
    };

    // Leave balance state - fetched from API
    const [leaveBalances, setLeaveBalances] = useState([]);
    const [balanceLoading, setBalanceLoading] = useState(false);
    const balanceYear = urlFilters.year || dayjs().year();

    // Comp-off earned credit state
    const [compOffBalance, setCompOffBalance] = useState(null);
    const [compOffEntries, setCompOffEntries] = useState([]);

    const fetchBalances = async (year) => {
        setBalanceLoading(true);
        try {
            const res = await loadMyLeaveBalances(year || balanceYear);
            if (res?.data) {
                const results = res.data.results || res.data;
                setLeaveBalances(Array.isArray(results) ? results : []);
            }
        } catch { /* handled */ }
        try {
            const compOff = await loadMyCompOffBalance(year || balanceYear);
            if (compOff) {
                setCompOffBalance(compOff.balance || null);
                setCompOffEntries(compOff.recent_entries || []);
            }
        } catch { /* handled */ }
        setBalanceLoading(false);
    };

    useEffect(() => {
        if (revealed) fetchBalances(balanceYear);
    }, [revealed, balanceYear]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── WFH state ────────────────────────────────────────────────────
    const [wfhRequests, setWfhRequests] = useState([]);
    const [wfhLoading, setWfhLoading] = useState(false);
    const [wfhPagination, setWfhPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [wfhBalance, setWfhBalance] = useState(null);
    const [wfhModalVisible, setWfhModalVisible] = useState(false);
    const [wfhModalType, setWfhModalType] = useState('apply'); // apply | edit | view
    const [wfhSelected, setWfhSelected] = useState(null);
    const [wfhForm] = Form.useForm();
    const [wfhSubmitting, setWfhSubmitting] = useState(false);

    const fetchWfhRequests = async (page = 1, pageSize = 10) => {
        if (!employee?.id) return;
        setWfhLoading(true);
        try {
            const res = await loadWFHRequests(page, pageSize, { employee: employee.id });
            if (res?.data) {
                const results = res.data.results || res.data;
                setWfhRequests(Array.isArray(results) ? results : []);
                setWfhPagination({ current: page, pageSize, total: res.data.count || 0 });
            }
        } catch { /* handled */ }
        setWfhLoading(false);
    };

    const fetchWfhBalance = async () => {
        try {
            const bal = await loadMyWFHBalance(dayjs().year());
            setWfhBalance(bal);
        } catch { /* */ }
    };

    useEffect(() => {
        if (activeSection === 'wfh' && employee?.id) {
            fetchWfhBalance();
        }
    }, [activeSection, employee?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (activeSection === 'wfh' && employee?.id) {
            fetchWfhRequests(urlFilters.wfhPage, wfhPageSize);
        }
    }, [activeSection, employee?.id, urlFilters.wfhPage, wfhPageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    const openWfhModal = (type = 'apply', record = null) => {
        if (isProfileLocked && (type === 'apply' || type === 'edit')) {
            message.warning('Cannot modify WFH applications - Profile is locked');
            return;
        }
        setWfhModalType(type);
        setWfhSelected(record);
        setWfhModalVisible(true);
        if (record && type === 'edit') {
            wfhForm.setFieldsValue({
                dateRange: [dayjs(record.start_date), dayjs(record.end_date)],
                reason: record.reason,
            });
        } else {
            wfhForm.resetFields();
        }
    };

    const closeWfhModal = () => {
        setWfhModalVisible(false);
        setWfhModalType('apply');
        setWfhSelected(null);
        wfhForm.resetFields();
    };

    const handleWfhSubmit = async (values) => {
        setWfhSubmitting(true);
        try {
            const payload = {
                employee: employee?.id,
                start_date: values.dateRange[0].format('YYYY-MM-DD'),
                end_date: values.dateRange[1].format('YYYY-MM-DD'),
                reason: values.reason,
            };
            if (wfhModalType === 'edit' && wfhSelected) {
                await updateWFHRequest(wfhSelected.id, payload);
                message.success('WFH request updated');
            } else {
                await createWFHRequest(payload);
                message.success('WFH request submitted');
            }
            closeWfhModal();
            fetchWfhRequests(wfhPagination.current, wfhPagination.pageSize);
            fetchWfhBalance();
        } catch (err) {
            const detail = err?.response?.data;
            const msg = (detail && typeof detail === 'object')
                ? Object.values(detail).flat().join('; ')
                : (detail?.detail || detail?.error || 'Failed to submit WFH request');
            message.error(msg);
        } finally {
            setWfhSubmitting(false);
        }
    };

    const handleWfhDelete = async (id) => {
        if (isProfileLocked) {
            message.warning('Cannot withdraw WFH applications - Profile is locked');
            return;
        }
        try {
            await deleteWFHRequest(id);
            message.success('WFH request withdrawn');
            fetchWfhRequests(wfhPagination.current, wfhPagination.pageSize);
        } catch {
            message.error('Failed to withdraw WFH request');
        }
    };

    // ─── Attendance state ─────────────────────────────────────────────
    const organization = useSelector(state => state.auth.organization);
    const [attendanceRecords, setAttendanceRecords] = useState([]);
    const [attendanceLoading, setAttendanceLoading] = useState(false);
    const [attendancePagination, setAttendancePagination] = useState({ current: 1, pageSize: 10, total: 0 });

    const fetchAttendance = async (page = 1, pageSize = 10) => {
        if (!employee?.id) return;
        setAttendanceLoading(true);
        try {
            const res = await loadAttendanceRecords(page, pageSize, organization, { employee: employee.id });
            if (res) {
                const results = res.results || res;
                setAttendanceRecords(Array.isArray(results) ? results : []);
                setAttendancePagination({ current: page, pageSize, total: res.count || 0 });
            }
        } catch { /* */ }
        setAttendanceLoading(false);
    };

    useEffect(() => {
        if (activeSection === 'attendance' && employee?.id) {
            fetchAttendance(urlFilters.attPage, attPageSize);
        }
    }, [activeSection, employee?.id, urlFilters.attPage, attPageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    const menuItems = [
        { key: 'overview', icon: <DashboardOutlined />, label: 'Overview' },
        { key: 'wfh', icon: <HomeOutlined />, label: 'WFH Applications' },
        { key: 'applications', icon: <FileTextOutlined />, label: 'Leave Applications' },
        { key: 'attendance', icon: <ClockCircleOutlined />, label: 'Attendance' },
    ];

    const columns = [
        {
            title: 'Leave Type',
            dataIndex: 'leave_type_name',
            key: 'leave_type_name',
            render: (name) => <Tag color="blue">{name || '—'}</Tag>,
            width: 130
        },
        {
            title: 'Duration',
            key: 'duration',
            render: (_, record) => (
                <div>
                    <div>{dayjs(record.start_date).format('DD MMM YYYY')} - {dayjs(record.end_date).format('DD MMM YYYY')}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{record.total_days} day(s)</div>
                </div>
            ),
            width: 180
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status, record) => (
                <div>
                    <Tag color={getStatusColor(status)} style={{ textTransform: 'capitalize' }}>
                        {status}
                    </Tag>
                    {status === 'approved' && record.approved_by_name && (
                        <div style={{ fontSize: '12px', color: '#52c41a' }}>
                            By: {record.approved_by_name}
                        </div>
                    )}
                    {status === 'rejected' && record.rejection_reason && (
                        <Tooltip title={record.rejection_reason}>
                            <InfoCircleOutlined style={{ color: '#ff4d4f', marginLeft: 4 }} />
                        </Tooltip>
                    )}
                </div>
            ),
            width: 120
        },
        {
            title: 'Reason',
            dataIndex: 'reason',
            key: 'reason',
            ellipsis: true,
            width: 200
        },
        {
            title: 'Applied Date',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => text ? dayjs(text).format('DD MMM YYYY') : '—',
            width: 120
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (record) => (
                <Space size="small">
                    <Tooltip title="View Details">
                        <Button
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => showModal('view', record)}
                        />
                    </Tooltip>
                    {record.status === 'pending' && !isProfileLocked && (
                        <>
                            <Tooltip title="Edit">
                                <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => showModal('edit', record)}
                                />
                            </Tooltip>
                            <Popconfirm
                                title="Cancel this leave application?"
                                description="This action cannot be undone."
                                onConfirm={() => handleDelete(record.id)}
                                okText="Yes, Cancel"
                                cancelText="No"
                                okButtonProps={{ danger: true }}
                            >
                                <Tooltip title="Cancel">
                                    <Button
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                    />
                                </Tooltip>
                            </Popconfirm>
                        </>
                    )}
                </Space>
            ),
            width: 120,
            fixed: 'right'
        },
    ];

    const BALANCE_COLORS = ['#3f8600', '#1890ff', '#faad14', '#722ed1', '#13c2c2', '#eb2f96', '#fa541c', '#52c41a'];

    const renderOverview = () => {
        const getUsagePercent = (bal) => {
            const total = parseFloat(bal.allocated_days || 0) + parseFloat(bal.carried_forward_days || 0);
            if (total === 0) return 0;
            return Math.round((parseFloat(bal.used_days || 0) / total) * 100);
        };

        return (
            <div>
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Text strong>Year:</Text>
                    <DatePicker
                        picker="year"
                        value={dayjs().year(balanceYear)}
                        onChange={(val) => {
                            const y = val ? val.year() : dayjs().year();
                            setUrlFilters({ year: y });
                        }}
                        style={{ width: 100 }}
                        allowClear={false}
                        size="small"
                    />
                </div>
                {balanceLoading ? (
                    <Row gutter={[16, 16]}>
                        {[1, 2, 3, 4].map(i => (
                            <Col xs={24} sm={12} lg={6} key={i}>
                                <Card loading={true} />
                            </Col>
                        ))}
                    </Row>
                ) : leaveBalances.length === 0 ? (
                    <Alert
                        message="No Leave Balances"
                        description={`No leave balances found for ${balanceYear}. Leave balances are created when monthly credits are generated.`}
                        type="info"
                        showIcon
                    />
                ) : (
                    <>
                        <Row gutter={[16, 16]}>
                            {leaveBalances.map((bal, idx) => {
                                const color = BALANCE_COLORS[idx % BALANCE_COLORS.length];
                                const total = parseFloat(bal.allocated_days || 0) + parseFloat(bal.carried_forward_days || 0);
                                const available = parseFloat(bal.available_days || 0);
                                const used = parseFloat(bal.used_days || 0);
                                const pending = parseFloat(bal.pending_days || 0);

                                return (
                                    <Col xs={24} sm={12} lg={6} key={bal.id}>
                                        <Card size="small">
                                            <Statistic
                                                title={bal.leave_type_name}
                                                value={available}
                                                suffix={total > 0 ? `/ ${total}` : ''}
                                                valueStyle={{ color }}
                                                prefix={<CalendarOutlined />}
                                            />
                                            <Progress
                                                percent={getUsagePercent(bal)}
                                                size="small"
                                                strokeColor={color}
                                                style={{ marginTop: 8 }}
                                            />
                                            <div style={{ marginTop: 8, fontSize: 12, color: '#666', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span>Allocated:</span>
                                                    <span>{bal.allocated_days} days</span>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span>Used:</span>
                                                    <span>{used} days</span>
                                                </div>
                                                {parseFloat(bal.carried_forward_days || 0) > 0 && (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <span>Carried Fwd:</span>
                                                        <span>{bal.carried_forward_days} days</span>
                                                    </div>
                                                )}
                                                {pending > 0 && (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#faad14' }}>
                                                        <span>Pending:</span>
                                                        <span>{pending} days</span>
                                                    </div>
                                                )}
                                            </div>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>

                        {compOffBalance && (
                            <Card
                                size="small"
                                title={<span><ClockCircleOutlined /> Earned Credit — Comp-Off</span>}
                                style={{ marginTop: 16 }}
                            >
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={8}>
                                        <Statistic
                                            title="Earned"
                                            value={parseFloat(compOffBalance.earned_days || 0)}
                                            suffix="days"
                                            valueStyle={{ color: '#3f8600' }}
                                        />
                                    </Col>
                                    <Col xs={24} sm={8}>
                                        <Statistic
                                            title="Used"
                                            value={parseFloat(compOffBalance.used_days || 0)}
                                            suffix="days"
                                            valueStyle={{ color: '#cf1322' }}
                                        />
                                    </Col>
                                    <Col xs={24} sm={8}>
                                        <Statistic
                                            title="Available"
                                            value={parseFloat(compOffBalance.available_days || 0)}
                                            suffix="days"
                                            valueStyle={{ color: '#1677ff' }}
                                        />
                                    </Col>
                                </Row>
                                {compOffEntries.length > 0 && (
                                    <div style={{ marginTop: 16 }}>
                                        <Text strong>Recent credits:</Text>
                                        <Table
                                            size="small"
                                            style={{ marginTop: 8 }}
                                            pagination={false}
                                            dataSource={compOffEntries}
                                            rowKey="id"
                                            columns={[
                                                {
                                                    title: 'Earned On',
                                                    dataIndex: 'earned_on',
                                                    key: 'earned_on',
                                                    render: (v) => dayjs(v).format('DD MMM YYYY'),
                                                },
                                                { title: 'Holiday', dataIndex: 'holiday_name', key: 'holiday_name', render: (v) => v || '—' },
                                                { title: 'Days', dataIndex: 'days', key: 'days' },
                                                { title: 'Used', dataIndex: 'consumed_days', key: 'consumed_days' },
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    render: (v) => {
                                                        const color = v === 'used' ? 'red' : v === 'partially_used' ? 'orange' : v === 'expired' ? 'default' : 'green';
                                                        return <Tag color={color}>{v}</Tag>;
                                                    },
                                                },
                                            ]}
                                        />
                                    </div>
                                )}
                            </Card>
                        )}

                        <Card size="small" style={{ marginTop: 16 }}>
                            <Table
                                columns={[
                                    { title: 'Leave Type', dataIndex: 'leave_type_name', key: 'leave_type_name' },
                                    { title: 'Allocated', dataIndex: 'allocated_days', key: 'allocated_days' },
                                    { title: 'Used', dataIndex: 'used_days', key: 'used_days' },
                                    { title: 'Carried Fwd', dataIndex: 'carried_forward_days', key: 'carried_forward_days' },
                                    { title: 'Encashed', dataIndex: 'encashed_days', key: 'encashed_days' },
                                    {
                                        title: 'Pending',
                                        dataIndex: 'pending_days',
                                        key: 'pending_days',
                                        render: (v) => parseFloat(v || 0) > 0 ? <Tag color="orange">{v}</Tag> : v,
                                    },
                                    {
                                        title: 'Available',
                                        dataIndex: 'available_days',
                                        key: 'available_days',
                                        render: (v) => <Text strong style={{ color: '#3f8600' }}>{v}</Text>,
                                    },
                                    {
                                        title: 'Credited Through',
                                        dataIndex: 'credited_through_month',
                                        key: 'credited_through_month',
                                        render: (v) => {
                                            const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                            return v ? MONTH_NAMES[v] || `Month ${v}` : '—';
                                        },
                                    },
                                ]}
                                dataSource={leaveBalances}
                                rowKey="id"
                                pagination={false}
                                size="small"
                                scroll={{ x: 700 }}
                            />
                        </Card>
                    </>
                )}
            </div>
        );
    };

    const renderApplications = () => {
        return (
            <Card
                // title="Leave Applications"
                extra={
                    !isProfileLocked && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={showModal}
                        >
                            Apply for Leave
                        </Button>
                    )
                }
            >
                <Table
                    columns={columns}
                    dataSource={leaves}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 800 }}
                    pagination={{
                        ...appPagination,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        onChange: pageSetter('page', 'pageSize', appPageSize),
                    }}
                />
            </Card>
        );
    };

    const getSectionTitle = () => {
        const titles = {
            'overview': 'Leave Overview',
            'wfh': 'WFH Applications',
            'applications': 'Leave Applications',
            'attendance': 'Attendance'
        };
        return titles[activeSection] || 'Timesheet';
    };

    const renderWfhApplications = () => {
        const wfhColumns = [
            {
                title: 'Dates',
                key: 'dates',
                render: (_, r) =>
                    r.start_date === r.end_date
                        ? dayjs(r.start_date).format('DD MMM YYYY')
                        : `${dayjs(r.start_date).format('DD MMM')} - ${dayjs(r.end_date).format('DD MMM YYYY')}`,
                width: 200,
            },
            { title: 'Days', dataIndex: 'total_days', key: 'total_days', width: 80 },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (s) => <Tag color={getStatusColor(s)} style={{ textTransform: 'capitalize' }}>{s}</Tag>,
                width: 110,
            },
            { title: 'Reason', dataIndex: 'reason', key: 'reason', ellipsis: true },
            {
                title: 'Applied',
                dataIndex: 'created_at',
                key: 'created_at',
                render: (t) => (t ? dayjs(t).format('DD MMM YYYY') : '—'),
                width: 120,
            },
            {
                title: 'Actions',
                key: 'actions',
                width: 130,
                fixed: 'right',
                render: (_, r) => (
                    <Space size="small">
                        <Tooltip title="View Details">
                            <Button size="small" icon={<EyeOutlined />} onClick={() => openWfhModal('view', r)} />
                        </Tooltip>
                        {r.status === 'pending' && !isProfileLocked && (
                            <>
                                <Tooltip title="Edit">
                                    <Button size="small" icon={<EditOutlined />} onClick={() => openWfhModal('edit', r)} />
                                </Tooltip>
                                <Popconfirm
                                    title="Withdraw this WFH request?"
                                    onConfirm={() => handleWfhDelete(r.id)}
                                    okText="Yes"
                                    cancelText="No"
                                    okButtonProps={{ danger: true }}
                                >
                                    <Tooltip title="Withdraw">
                                        <Button size="small" danger icon={<DeleteOutlined />} />
                                    </Tooltip>
                                </Popconfirm>
                            </>
                        )}
                    </Space>
                ),
            },
        ];

        return (
            <>
                {wfhBalance && (
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={12} sm={6}>
                            <Card size="small">
                                <Statistic
                                    title="Allocated"
                                    value={wfhBalance.allocated_days || 0}
                                    suffix="days"
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small">
                                <Statistic
                                    title="Used"
                                    value={wfhBalance.used_days || 0}
                                    suffix="days"
                                    valueStyle={{ color: '#faad14' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small">
                                <Statistic
                                    title="Available"
                                    value={wfhBalance.available_days || 0}
                                    suffix="days"
                                    valueStyle={{ color: '#3f8600' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small">
                                <Statistic
                                    title="Pending"
                                    value={wfhBalance.pending_days || 0}
                                    suffix="days"
                                    valueStyle={{ color: '#fa8c16' }}
                                />
                            </Card>
                        </Col>
                    </Row>
                )}

                <Card
                    // title="WFH Applications"
                    extra={
                        !isProfileLocked && (
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => openWfhModal('apply')}>
                                Apply for WFH
                            </Button>
                        )
                    }
                >
                    <Table
                        columns={wfhColumns}
                        dataSource={wfhRequests}
                        loading={wfhLoading}
                        rowKey="id"
                        scroll={{ x: 800 }}
                        pagination={{
                            ...wfhPagination,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            onChange: pageSetter('wfhPage', 'wfhPageSize', wfhPageSize),
                        }}
                    />
                </Card>

                <Modal
                    title={
                        wfhModalType === 'view'
                            ? 'WFH Request Details'
                            : wfhModalType === 'edit'
                                ? 'Edit WFH Request'
                                : 'Apply for WFH'
                    }
                    open={wfhModalVisible}
                    onCancel={closeWfhModal}
                    footer={wfhModalType === 'view' ? [
                        <Button key="close" onClick={closeWfhModal}>Close</Button>
                    ] : null}
                    width={600}
                    destroyOnClose
                >
                    {wfhModalType === 'view' && wfhSelected ? (
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <div><strong>Status:</strong></div>
                                <Tag color={getStatusColor(wfhSelected.status)} style={{ marginTop: 4, textTransform: 'capitalize' }}>
                                    {wfhSelected.status}
                                </Tag>
                            </Col>
                            <Col span={12}>
                                <div><strong>Days:</strong></div>
                                <div>{wfhSelected.total_days}</div>
                            </Col>
                            <Col span={12}>
                                <div><strong>Start:</strong></div>
                                <div>{dayjs(wfhSelected.start_date).format('DD MMM YYYY')}</div>
                            </Col>
                            <Col span={12}>
                                <div><strong>End:</strong></div>
                                <div>{dayjs(wfhSelected.end_date).format('DD MMM YYYY')}</div>
                            </Col>
                            <Col span={24}>
                                <div><strong>Reason:</strong></div>
                                <div style={{ marginTop: 4, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                                    {wfhSelected.reason}
                                </div>
                            </Col>
                            {wfhSelected.rejection_reason && (
                                <Col span={24}>
                                    <div><strong>Rejection Reason:</strong></div>
                                    <div style={{ color: '#ff4d4f' }}>{wfhSelected.rejection_reason}</div>
                                </Col>
                            )}
                            {wfhSelected.approved_by_name && (
                                <Col span={24}>
                                    <div>
                                        <strong>{wfhSelected.status === 'approved' ? 'Approved' : 'Actioned'} by:</strong>{' '}
                                        {wfhSelected.approved_by_name} on{' '}
                                        {wfhSelected.approved_at ? dayjs(wfhSelected.approved_at).format('DD MMM YYYY') : '—'}
                                    </div>
                                </Col>
                            )}
                        </Row>
                    ) : (
                        <Form form={wfhForm} layout="vertical" onFinish={handleWfhSubmit}>
                            <Form.Item
                                label="WFH Dates"
                                name="dateRange"
                                rules={[{ required: true, message: 'Select date range' }]}
                            >
                                <RangePicker
                                    style={{ width: '100%' }}
                                    size="large"
                                    disabledDate={(current) => current && current < dayjs().startOf('day')}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Reason"
                                name="reason"
                                rules={[
                                    { required: true, message: 'Enter reason' },
                                    { min: 10, message: 'Reason must be at least 10 characters' },
                                ]}
                            >
                                <TextArea rows={4} placeholder="Reason for working from home" maxLength={500} showCount />
                            </Form.Item>
                            <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
                                <Space>
                                    <Button onClick={closeWfhModal} size="large">Cancel</Button>
                                    <Button type="primary" htmlType="submit" loading={wfhSubmitting} size="large">
                                        {wfhModalType === 'edit' ? 'Update' : 'Submit'}
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    )}
                </Modal>
            </>
        );
    };

    const renderAttendance = () => {
        const attendanceColumns = [
            {
                title: 'Date',
                dataIndex: 'date',
                key: 'date',
                render: (d) => (d ? dayjs(d).format('DD MMM YYYY (ddd)') : '—'),
                width: 180,
            },
            {
                title: 'Check In',
                dataIndex: 'check_in',
                key: 'check_in',
                render: (t) => (t ? dayjs(t).format('HH:mm') : '—'),
                width: 100,
            },
            {
                title: 'Check Out',
                dataIndex: 'check_out',
                key: 'check_out',
                render: (t) => (t ? dayjs(t).format('HH:mm') : '—'),
                width: 100,
            },
            {
                title: 'Working Hours',
                dataIndex: 'working_hours',
                key: 'working_hours',
                render: (v) => (v != null ? Number(v).toFixed(2) : '—'),
                width: 130,
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (s) => s ? <Tag color={getStatusColor(s)} style={{ textTransform: 'capitalize' }}>{s}</Tag> : '—',
                width: 110,
            },
        ];

        return (
            <Card 
            // title="Attendance"
            >
                <Table
                    columns={attendanceColumns}
                    dataSource={attendanceRecords}
                    loading={attendanceLoading}
                    rowKey={(r) => r.id || `${r.date}-${r.employee_id}`}
                    scroll={{ x: 700 }}
                    pagination={{
                        ...attendancePagination,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        onChange: pageSetter('attPage', 'attPageSize', attPageSize),
                    }}
                />
            </Card>
        );
    };

    const renderContent = () => {
        switch (activeSection) {
            case 'overview':
                return renderOverview();
            case 'wfh':
                return renderWfhApplications();
            case 'applications':
                return renderApplications();
            case 'attendance':
                return renderAttendance();
            default:
                return <Text>Section not found</Text>;
        }
    };

    return (
        <div style={{ height: 'calc(100vh - 280px)', padding: '20px' }}>
            {/* {isProfileLocked && (
                <Alert
                    message="Profile Locked"
                    description="You cannot apply for new leaves or modify existing applications while your profile is locked."
                    type="warning"
                    showIcon
                    style={{ marginBottom: '16px' }}
                />
            )} */}
            <Card
                style={{ height: isProfileLocked ? 'calc(100% - 80px)' : '100%', display: 'flex', flexDirection: 'column' }}
                bodyStyle={{ flex: 1, padding: 0, overflow: 'hidden' }}
            >
                <Layout style={{ flex: 1, background: '#fff', overflow: 'hidden' }}>
                    {/* Sidebar */}
                    <Sider
                        width={240}
                        style={{
                            background: '#fff',
                            borderRight: '1px solid #f0f0f0',
                            overflow: 'auto'
                        }}
                    >
                        <style>
                            {`
                                .leaves-sidebar-menu .ant-menu-item-selected {
                                    background-color: #1890ff !important;
                                    color: #fff !important;
                                }
                                .leaves-sidebar-menu .ant-menu-item-selected .anticon {
                                    color: #fff !important;
                                }
                                .leaves-sidebar-menu .ant-menu-item-selected::after {
                                    border-right-color: #1890ff !important;
                                }
                            `}
                        </style>
                        <Menu
                            mode="inline"
                            selectedKeys={[activeSection]}
                            onClick={({ key }) => setActiveSection(key)}
                            style={{ borderRight: 0 }}
                            items={menuItems}
                            className="leaves-sidebar-menu"
                        />
                    </Sider>

                    {/* Content Area */}
                    <Content style={{ padding: '24px', overflow: 'auto', background: '#fff' }}>
                        <Title level={5} style={{ marginBottom: 24 }}>
                            {getSectionTitle()}
                        </Title>
                        {renderContent()}
                    </Content>
                </Layout>
            </Card>

            {/* Modal for Apply/Edit/View Leave */}
            <Modal
                title={
                    modalType === 'view'
                        ? 'Leave Application Details'
                        : modalType === 'edit'
                            ? 'Edit Leave Application'
                            : 'Apply for Leave'
                }
                open={isModalVisible}
                onCancel={handleCancel}
                footer={modalType === 'view' ? [
                    <Button key="close" onClick={handleCancel}>
                        Close
                    </Button>
                ] : null}
                width={600}
            >
                {modalType === 'view' && selectedLeave ? (
                    <div>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <div><strong>Leave Type:</strong></div>
                                <Tag color="blue" style={{ marginTop: 4 }}>{selectedLeave.leave_type_name}</Tag>
                            </Col>
                            <Col span={12}>
                                <div><strong>Status:</strong></div>
                                <Tag color={getStatusColor(selectedLeave.status)} style={{ marginTop: 4, textTransform: 'capitalize' }}>
                                    {selectedLeave.status}
                                </Tag>
                            </Col>
                            <Col span={12}>
                                <div><strong>Start Date:</strong></div>
                                <div>{dayjs(selectedLeave.start_date).format('DD MMM YYYY')}</div>
                            </Col>
                            <Col span={12}>
                                <div><strong>End Date:</strong></div>
                                <div>{dayjs(selectedLeave.end_date).format('DD MMM YYYY')}</div>
                            </Col>
                            <Col span={12}>
                                <div><strong>Duration:</strong></div>
                                <div>{selectedLeave.total_days} day(s) — {selectedLeave.leave_duration?.replace(/_/g, ' ')}</div>
                            </Col>
                            <Col span={12}>
                                <div><strong>Applied Date:</strong></div>
                                <div>{dayjs(selectedLeave.created_at).format('DD MMM YYYY')}</div>
                            </Col>
                            {selectedLeave.approved_by_name && (
                                <>
                                    <Col span={12}>
                                        <div><strong>Approved By:</strong></div>
                                        <div>{selectedLeave.approved_by_name}</div>
                                    </Col>
                                    <Col span={12}>
                                        <div><strong>Approved Date:</strong></div>
                                        <div>{selectedLeave.approved_at ? dayjs(selectedLeave.approved_at).format('DD MMM YYYY') : '—'}</div>
                                    </Col>
                                </>
                            )}
                            {selectedLeave.rejection_reason && (
                                <Col span={24}>
                                    <div><strong>Rejection Reason:</strong></div>
                                    <div style={{ color: '#ff4d4f' }}>{selectedLeave.rejection_reason}</div>
                                </Col>
                            )}
                            <Col span={24}>
                                <div><strong>Reason:</strong></div>
                                <div style={{ marginTop: 4, padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
                                    {selectedLeave.reason}
                                </div>
                            </Col>
                            {selectedLeave.supporting_document && (
                                <Col span={24}>
                                    <div><strong>Supporting Document:</strong></div>
                                    <div style={{ marginTop: 4 }}>{selectedLeave.supporting_document}</div>
                                </Col>
                            )}
                        </Row>
                    </div>
                ) : (
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                    >
                        <Form.Item
                            label="Leave Type"
                            name="type"
                            rules={[{ required: true, message: 'Please select leave type!' }]}
                        >
                            <Select
                                placeholder="Select leave type"
                                size="large"
                                onChange={(val) => setSelectedLeaveTypeId(val)}
                            >
                                {leaveTypes.map(lt => (
                                    <Option key={lt.id} value={lt.id}>
                                        {lt.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        {selectedLeaveTypeId && (() => {
                            const lt = leaveTypes.find(t => t.id === selectedLeaveTypeId);
                            if (!lt) return null;
                            return (
                                <div style={{
                                    marginTop: -16,
                                    marginBottom: 16,
                                    padding: '10px 12px',
                                    background: '#f6f8fa',
                                    borderRadius: 6,
                                    border: '1px solid #e8e8e8',
                                    fontSize: 13,
                                }}>
                                    {lt.description && (
                                        <div style={{ marginBottom: 6, color: '#555' }}>{lt.description}</div>
                                    )}
                                    <Row gutter={[12, 4]}>
                                        <Col span={8}><Text type="secondary">Max/Year:</Text> <Text strong>{lt.max_days_per_year}</Text></Col>
                                        <Col span={8}><Text type="secondary">Monthly Credit:</Text> <Text strong>{lt.monthly_credit_days}</Text></Col>
                                        <Col span={8}><Text type="secondary">Max Consecutive:</Text> <Text strong>{lt.max_consecutive_days}</Text></Col>
                                        <Col span={8}><Text type="secondary">Half Day:</Text> <Text strong>{lt.allows_half_day ? 'Yes' : 'No'}</Text></Col>
                                        <Col span={8}><Text type="secondary">Carry Forward:</Text> <Text strong>{lt.is_carry_forward_allowed ? 'Yes' : 'No'}</Text></Col>
                                        <Col span={8}><Text type="secondary">Encashable:</Text> <Text strong>{lt.is_encashable ? 'Yes' : 'No'}</Text></Col>
                                    </Row>
                                </div>
                            );
                        })()}

                        <Form.Item
                            label="Date Range"
                            name="dateRange"
                            rules={[{ required: true, message: 'Please select date range!' }]}
                        >
                            <RangePicker
                                style={{ width: '100%' }}
                                size="large"
                                disabledDate={(current) => {
                                    if (user?.is_staff || user?.is_superuser) return false;
                                    return current && current < dayjs().startOf('day');
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Reason"
                            name="reason"
                            rules={[
                                { required: true, message: 'Please provide a reason!' },
                                { min: 10, message: 'Reason must be at least 10 characters!' }
                            ]}
                        >
                            <TextArea
                                rows={4}
                                placeholder="Please provide the reason for your leave application..."
                                maxLength={500}
                                showCount
                            />
                        </Form.Item>

                        <Form.Item
                            label="Supporting Document (Optional)"
                            name="document"
                        >
                            <Upload
                                fileList={fileList}
                                onChange={handleFileChange}
                                beforeUpload={() => false}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                maxCount={1}
                            >
                                <Button icon={<UploadOutlined />} size="large">
                                    Upload Document
                                </Button>
                            </Upload>
                            <div style={{ marginTop: 4, fontSize: '12px', color: '#666' }}>
                                Supported formats: PDF, DOC, DOCX, JPG, PNG (Max: 5MB)
                            </div>
                        </Form.Item>

                        <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
                            <Space>
                                <Button onClick={handleCancel} size="large">
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    size="large"
                                >
                                    {modalType === 'edit' ? 'Update Application' : 'Submit Application'}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                )}
            </Modal>

            {/* Identity Verification Modal (password, or emailed code for 2FA users) */}
            <Modal
                title="Verify Your Identity"
                open={passwordModalVisible}
                onCancel={() => {
                    setPasswordModalVisible(false);
                    setPendingModalAction(null);
                }}
                footer={null}
                width={400}
                destroyOnClose
            >
                <VerifyIdentityCard
                    embedded
                    description="Enter your password to apply for leave"
                    onVerified={handleIdentityVerified}
                />
            </Modal>
        </div>
    );
};

export default Leaves;
